import { useState, useRef, useEffect, useMemo } from "react";
import {
  Brain,
  Zap,
  Sparkles,
  Search,
  X,
  ChevronDown,
  Check,
  RefreshCw,
  Cpu,
  Layers,
  Wrench,
  PlusCircle,
  Radio,
  Share2,
} from "lucide-react";
import type { ModelCatalogResponse, ModelCatalogItem } from "../../gen/models";
import {
  getModelIntelligence,
  type ModelIntelligence,
} from "../../utils/modelInfo";

export interface ModelSelectorProps {
  value: string;
  onChange: (modelId: string) => void;
  catalog: ModelCatalogResponse | null;
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
}

export function ModelSelector({
  value,
  onChange,
  catalog,
  isLoading = false,
  disabled = false,
  className = "",
}: ModelSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    } else {
      setSearchQuery("");
      setActiveFilter("all");
    }
  }, [isOpen]);

  // Keyboard shortcut: Escape to close
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  // Combine and deduplicate models
  const { allModels, curatedList, nonCuratedList } = useMemo(() => {
    const map = new Map<string, { raw: ModelCatalogItem; intelligence: ModelIntelligence }>();

    // 1. Add backend curated models if present
    if (catalog?.curated_models) {
      for (const m of catalog.curated_models) {
        map.set(m.id, {
          raw: m,
          intelligence: getModelIntelligence(m),
        });
      }
    }

    // 3. Add all backend models
    if (catalog?.models) {
      for (const m of catalog.models) {
        const existing = map.get(m.id);
        const isCurated = existing?.intelligence.isCurated || Boolean(m.is_curated);
        const mergedItem: ModelCatalogItem = {
          ...m,
          is_curated: isCurated,
        };
        map.set(m.id, {
          raw: mergedItem,
          intelligence: getModelIntelligence(mergedItem),
        });
      }
    }

    // 4. Ensure current value is included if not in catalog
    if (value && !map.has(value)) {
      const customItem: ModelCatalogItem = {
        id: value,
        name: value,
        provider: value.includes("/") ? "OpenRouter" : "Google",
        context_length: 128000,
        is_curated: false,
      };
      map.set(value, {
        raw: customItem,
        intelligence: getModelIntelligence(customItem),
      });
    }

    const items = Array.from(map.values());
    const curated = items.filter((x) => x.intelligence.isCurated);
    const nonCurated = items.filter((x) => !x.intelligence.isCurated);

    return {
      allModels: items,
      curatedList: curated,
      nonCuratedList: nonCurated,
    };
  }, [catalog, value]);

  // Counts for direct routes and filter pills
  const googleDirectCount = useMemo(
    () => allModels.filter((m) => m.intelligence.integrationRoute === "google_direct").length,
    [allModels]
  );
  const openRouterCount = useMemo(
    () => allModels.filter((m) => m.intelligence.integrationRoute === "openrouter").length,
    [allModels]
  );
  const reasoningCount = useMemo(
    () => allModels.filter((m) => m.intelligence.isReasoning).length,
    [allModels]
  );
  const structuredOutputCount = useMemo(
    () => allModels.filter((m) => m.intelligence.supportsStructuredOutputs).length,
    [allModels]
  );
  const curatedCount = useMemo(
    () => allModels.filter((m) => m.intelligence.isCurated).length,
    [allModels]
  );

  // Filtered models based on free-text query and filter chip
  const filteredModels = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return allModels.filter((item) => {
      // 1. Filter Chip
      if (activeFilter === "reasoning" && !item.intelligence.isReasoning) {
        return false;
      }
      if (activeFilter === "structured_output" && !item.intelligence.supportsStructuredOutputs) {
        return false;
      }
      if (activeFilter === "curated" && !item.intelligence.isCurated) {
        return false;
      }
      if (activeFilter === "google_direct" && item.intelligence.integrationRoute !== "google_direct") {
        return false;
      }
      if (activeFilter === "openrouter" && item.intelligence.integrationRoute !== "openrouter") {
        return false;
      }
      if (
        activeFilter !== "all" &&
        activeFilter !== "reasoning" &&
        activeFilter !== "structured_output" &&
        activeFilter !== "curated" &&
        activeFilter !== "google_direct" &&
        activeFilter !== "openrouter" &&
        item.intelligence.underlyingProvider.toLowerCase() !== activeFilter.toLowerCase()
      ) {
        return false;
      }

      // 2. Free Text Search Query
      if (!q) return true;

      const idMatch = item.intelligence.id.toLowerCase().includes(q);
      const nameMatch = item.intelligence.name.toLowerCase().includes(q);
      const routeMatch =
        item.intelligence.integrationLabel.toLowerCase().includes(q) ||
        (q === "google" && item.intelligence.integrationRoute === "google_direct") ||
        (q === "openrouter" && item.intelligence.integrationRoute === "openrouter") ||
        (q === "direct" && item.intelligence.integrationRoute === "google_direct");
      const labMatch = item.intelligence.underlyingProvider.toLowerCase().includes(q);
      const reasoningMatch =
        item.intelligence.reasoningLabel.toLowerCase().includes(q) ||
        (q === "reasoning" && item.intelligence.isReasoning) ||
        (q === "standard" && !item.intelligence.isReasoning);
      const curatedMatch =
        (q === "curated" || q === "recommended") && item.intelligence.isCurated;

      return idMatch || nameMatch || routeMatch || labMatch || reasoningMatch || curatedMatch;
    });
  }, [allModels, searchQuery, activeFilter]);

  // Selected model intelligence
  const currentModelIntelligence = useMemo(() => {
    return getModelIntelligence(
      value,
      allModels.map((m) => m.raw)
    );
  }, [value, allModels]);

  // Has exact query match for custom model creation
  const exactMatchExists = useMemo(() => {
    if (!searchQuery.trim()) return true;
    return allModels.some(
      (m) => m.intelligence.id.toLowerCase() === searchQuery.trim().toLowerCase()
    );
  }, [searchQuery, allModels]);

  const handleSelect = (modelId: string) => {
    onChange(modelId);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative space-y-2 ${className}`}>
      {/* ------------------------------------------------------------- */}
      {/* Combobox Trigger Box                                          */}
      {/* ------------------------------------------------------------- */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((prev) => !prev)}
        className={`w-full text-left bg-white dark:bg-stone-900 border rounded-xl p-3 shadow-2xs transition-all focus:outline-hidden focus:ring-2 focus:ring-stone-400 dark:focus:ring-stone-600 ${
          isOpen
            ? "border-stone-400 dark:border-stone-600 ring-1 ring-stone-400"
            : "border-stone-200 dark:border-stone-800 hover:border-stone-300 dark:hover:border-stone-700"
        } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            {/* Reasoning vs Standard Icon */}
            <div
              className={`p-1.5 rounded-lg shrink-0 ${
                currentModelIntelligence.isReasoning
                  ? "bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300"
                  : "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300"
              }`}
            >
              {currentModelIntelligence.isReasoning ? (
                <Brain className="w-4 h-4" />
              ) : (
                <Zap className="w-4 h-4" />
              )}
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold text-stone-900 dark:text-stone-100 truncate">
                  {currentModelIntelligence.name}
                </span>

                {/* Direct Google vs OpenRouter Badge */}
                {currentModelIntelligence.integrationRoute === "google_direct" ? (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-sky-50 text-sky-700 border border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800/60">
                    <Radio className="w-2.5 h-2.5 text-sky-600" />
                    Google (Direct API)
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/60">
                    <Share2 className="w-2.5 h-2.5 text-emerald-600" />
                    via OpenRouter
                    <span className="font-normal opacity-80">
                      ({currentModelIntelligence.underlyingProvider})
                    </span>
                  </span>
                )}

                {/* Reasoning Badge */}
                {currentModelIntelligence.isReasoning ? (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800/60">
                    <Brain className="w-2.5 h-2.5" />
                    {currentModelIntelligence.reasoningLabel}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-stone-100 text-stone-600 border border-stone-200 dark:bg-stone-800 dark:text-stone-300 dark:border-stone-700">
                    <Zap className="w-2.5 h-2.5" />
                    Standard
                  </span>
                )}

                {/* Structured Outputs Tag */}
                {currentModelIntelligence.supportsStructuredOutputs ? (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-teal-50 text-teal-700 border border-teal-200 dark:bg-teal-950/30 dark:text-teal-400 dark:border-teal-800/50" title="Native JSON schema structured output supported">
                    <Layers className="w-2.5 h-2.5 text-teal-600" />
                    Structured Output
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800/50" title="Fallback tool-calling wrapper required for structured output">
                    <Wrench className="w-2.5 h-2.5 text-amber-500" />
                    Tool-Wrapped
                  </span>
                )}

                {/* Curated Tag */}
                {currentModelIntelligence.isCurated && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800/50">
                    <Sparkles className="w-2.5 h-2.5 text-amber-500" />
                    Curated
                  </span>
                )}
              </div>

              {/* ID Subtitle */}
              <p className="text-[11px] font-mono text-stone-500 dark:text-stone-400 truncate mt-0.5">
                {currentModelIntelligence.id}
              </p>
            </div>
          </div>

          {/* Right Summary & Arrow */}
          <div className="flex items-center gap-3 shrink-0">
            <div className="text-right hidden sm:block">
              <div className="text-[11px] font-medium text-stone-700 dark:text-stone-300">
                {currentModelIntelligence.formattedContext}
              </div>
              <div className="text-[10px] text-stone-400 font-mono">
                {currentModelIntelligence.formattedPrice}
              </div>
            </div>

            <ChevronDown
              className={`w-4 h-4 text-stone-400 transition-transform duration-200 ${
                isOpen ? "rotate-180 text-stone-700 dark:text-stone-200" : ""
              }`}
            />
          </div>
        </div>
      </button>

      {/* ------------------------------------------------------------- */}
      {/* Dropdown Popover with Free Text Search & Filtering            */}
      {/* ------------------------------------------------------------- */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1.5 z-50 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl shadow-xl overflow-hidden flex flex-col max-h-[460px] animate-in fade-in-50 zoom-in-95 duration-100">
          {/* Search Header */}
          <div className="p-3 border-b border-stone-100 dark:border-stone-800 space-y-2.5 bg-stone-50/70 dark:bg-stone-900/90">
            <div className="relative">
              <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search models by name, ID, OpenRouter lab, reasoning..."
                className="w-full pl-9 pr-8 py-2 text-xs bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 placeholder-stone-400 focus:outline-hidden focus:ring-1 focus:ring-stone-400 dark:focus:ring-stone-600 shadow-2xs font-sans"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 p-0.5 cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Quick Filter Chips */}
            <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-0.5 text-[11px]">
              <button
                type="button"
                onClick={() => setActiveFilter("all")}
                className={`px-2 py-0.5 rounded-md font-medium transition-colors shrink-0 cursor-pointer ${
                  activeFilter === "all"
                    ? "bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900"
                    : "bg-stone-200/70 dark:bg-stone-800 text-stone-600 dark:text-stone-400 hover:bg-stone-300/70 dark:hover:bg-stone-700"
                }`}
              >
                All ({allModels.length})
              </button>

              {/* Direct Google Provider Filter */}
              <button
                type="button"
                onClick={() => setActiveFilter("google_direct")}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-medium transition-colors shrink-0 cursor-pointer ${
                  activeFilter === "google_direct"
                    ? "bg-sky-600 text-white"
                    : "bg-sky-100 text-sky-800 dark:bg-sky-950/50 dark:text-sky-300 hover:bg-sky-200 dark:hover:bg-sky-900/60"
                }`}
              >
                <Radio className="w-3 h-3" />
                Google (Direct) ({googleDirectCount})
              </button>

              {/* OpenRouter Provider Filter */}
              <button
                type="button"
                onClick={() => setActiveFilter("openrouter")}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-medium transition-colors shrink-0 cursor-pointer ${
                  activeFilter === "openrouter"
                    ? "bg-emerald-600 text-white"
                    : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-900/60"
                }`}
              >
                <Share2 className="w-3 h-3" />
                OpenRouter ({openRouterCount})
              </button>

              {/* Reasoning Filter */}
              <button
                type="button"
                onClick={() => setActiveFilter("reasoning")}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-medium transition-colors shrink-0 cursor-pointer ${
                  activeFilter === "reasoning"
                    ? "bg-purple-600 text-white"
                    : "bg-purple-100 text-purple-700 dark:bg-purple-950/50 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/60"
                }`}
              >
                <Brain className="w-3 h-3" />
                Reasoning ({reasoningCount})
              </button>

              {/* Structured Output Filter */}
              <button
                type="button"
                onClick={() => setActiveFilter("structured_output")}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-medium transition-colors shrink-0 cursor-pointer ${
                  activeFilter === "structured_output"
                    ? "bg-teal-600 text-white"
                    : "bg-teal-100 text-teal-800 dark:bg-teal-950/50 dark:text-teal-300 hover:bg-teal-200 dark:hover:bg-teal-900/60"
                }`}
              >
                <Layers className="w-3 h-3" />
                Structured Output ({structuredOutputCount})
              </button>

              {/* Curated Filter */}
              <button
                type="button"
                onClick={() => setActiveFilter("curated")}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-medium transition-colors shrink-0 cursor-pointer ${
                  activeFilter === "curated"
                    ? "bg-amber-600 text-white"
                    : "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/60"
                }`}
              >
                <Sparkles className="w-3 h-3" />
                Curated ({curatedCount})
              </button>
            </div>
          </div>

          {/* Model Options List */}
          <div className="overflow-y-auto max-h-[320px] divide-y divide-stone-100 dark:divide-stone-800/60">
            {/* If searching or filtering, display single list */}
            {searchQuery || activeFilter !== "all" ? (
              filteredModels.length > 0 ? (
                <div>
                  <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-stone-400 dark:text-stone-500 bg-stone-50/50 dark:bg-stone-900/50">
                    Results ({filteredModels.length})
                  </div>
                  {filteredModels.map((item) => (
                    <ModelOptionRow
                      key={item.intelligence.id}
                      item={item.intelligence}
                      isSelected={item.intelligence.id === value}
                      onSelect={() => handleSelect(item.intelligence.id)}
                    />
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center space-y-3">
                  <p className="text-xs text-stone-500 dark:text-stone-400">
                    No models match <span className="font-semibold font-mono">"{searchQuery}"</span>
                  </p>
                  {searchQuery.trim() && !exactMatchExists && (
                    <button
                      type="button"
                      onClick={() => handleSelect(searchQuery.trim())}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-stone-900 dark:text-stone-100 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 rounded-lg cursor-pointer transition-colors shadow-2xs"
                    >
                      <PlusCircle className="w-3.5 h-3.5 text-emerald-600" />
                      Use custom model ID: <span className="font-mono">{searchQuery.trim()}</span>
                    </button>
                  )}
                </div>
              )
            ) : (
              /* No search query & All filter: display Curated and All Available groups */
              <>
                {curatedList.length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 bg-amber-50/60 dark:bg-amber-950/20 flex items-center justify-between">
                      <span className="flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        Curated Models (Recommended)
                      </span>
                      <span className="text-[10px] font-normal text-stone-400">
                        {curatedList.length} models
                      </span>
                    </div>
                    {curatedList.map((item) => (
                      <ModelOptionRow
                        key={item.intelligence.id}
                        item={item.intelligence}
                        isSelected={item.intelligence.id === value}
                        onSelect={() => handleSelect(item.intelligence.id)}
                      />
                    ))}
                  </div>
                )}

                {nonCuratedList.length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400 bg-stone-50/60 dark:bg-stone-900/60 flex items-center justify-between">
                      <span className="flex items-center gap-1">
                        <Layers className="w-3 h-3" />
                        All Available Foundation Models
                      </span>
                      <span className="text-[10px] font-normal text-stone-400">
                        {nonCuratedList.length} models
                      </span>
                    </div>
                    {nonCuratedList.map((item) => (
                      <ModelOptionRow
                        key={item.intelligence.id}
                        item={item.intelligence}
                        isSelected={item.intelligence.id === value}
                        onSelect={() => handleSelect(item.intelligence.id)}
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Custom option prompt if typed something not yet selected */}
            {searchQuery.trim() && !exactMatchExists && filteredModels.length > 0 && (
              <div className="p-2 bg-stone-50 dark:bg-stone-800/40 border-t border-stone-100 dark:border-stone-800">
                <button
                  type="button"
                  onClick={() => handleSelect(searchQuery.trim())}
                  className="w-full flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-stone-700 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-lg cursor-pointer transition-colors"
                >
                  <PlusCircle className="w-3.5 h-3.5 text-emerald-600" />
                  Use custom model string: <span className="font-mono">{searchQuery.trim()}</span>
                </button>
              </div>
            )}
          </div>

          {/* Footer Status */}
          <div className="p-2 border-t border-stone-100 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-900/50 flex items-center justify-between text-[11px] text-stone-500 dark:text-stone-400">
            <span className="flex items-center gap-1.5">
              <Cpu className="w-3 h-3 text-stone-400" />
              {allModels.length} models (Google Direct & OpenRouter)
            </span>
            {isLoading && (
              <span className="flex items-center gap-1 text-stone-400">
                <RefreshCw className="w-3 h-3 animate-spin" />
                Refreshing catalog...
              </span>
            )}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* Selected Model Details & Reasoning Deep-Dive Card             */}
      {/* ------------------------------------------------------------- */}
      <div className="bg-stone-50/70 dark:bg-stone-900/40 rounded-xl p-3 space-y-2.5">
        {/* Reasoning Status Banner */}
        <div
          className={`flex items-start gap-2 p-2 rounded-lg text-xs ${
            currentModelIntelligence.isReasoning
              ? "bg-purple-50/80 dark:bg-purple-950/30 text-purple-900 dark:text-purple-200"
              : "bg-stone-100/80 dark:bg-stone-800/50 text-stone-800 dark:text-stone-200"
          }`}
        >
          <div className="shrink-0 mt-0.5">
            {currentModelIntelligence.isReasoning ? (
              <Brain className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            ) : (
              <Zap className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            )}
          </div>
          <div className="space-y-0.5 min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-bold">
                {currentModelIntelligence.isReasoning
                  ? `${currentModelIntelligence.reasoningLabel} Active`
                  : "Standard Direct Output Active"}
              </span>
              <span
                className={`text-[10px] font-semibold px-1.5 py-0.2 rounded ${
                  currentModelIntelligence.isReasoning
                    ? "bg-purple-200/60 dark:bg-purple-900/60 text-purple-800 dark:text-purple-200"
                    : "bg-stone-200 dark:bg-stone-700 text-stone-700 dark:text-stone-300"
                }`}
              >
                {currentModelIntelligence.isReasoning ? "Chain-of-Thought" : "Low Latency"}
              </span>
            </div>
            <p className="text-[11px] leading-relaxed opacity-90">
              {currentModelIntelligence.reasoningDescription}
            </p>
          </div>
        </div>

        {/* Specifications Grid: Access Route, Underlying Lab, Context, Rate Card */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
          {/* Direct Provider / Access Route */}
          <div className="space-y-0.5">
            <span className="text-stone-400 block font-medium">Access Route</span>
            <div className="flex items-center gap-1 font-semibold">
              {currentModelIntelligence.integrationRoute === "google_direct" ? (
                <span className="text-sky-700 dark:text-sky-300 flex items-center gap-1">
                  <Radio className="w-3 h-3 text-sky-600" />
                  Google Direct API
                </span>
              ) : (
                <span className="text-emerald-700 dark:text-emerald-300 flex items-center gap-1">
                  <Share2 className="w-3 h-3 text-emerald-600" />
                  OpenRouter
                </span>
              )}
            </div>
          </div>

          {/* Under-the-hood Laboratory / Model Creator */}
          <div className="space-y-0.5">
            <span className="text-stone-400 block font-medium">Underlying Lab</span>
            <span className="font-semibold text-stone-800 dark:text-stone-200 block">
              {currentModelIntelligence.underlyingProvider}
            </span>
          </div>

          {/* Context Length */}
          <div className="space-y-0.5">
            <span className="text-stone-400 block font-medium">Context Window</span>
            <span className="font-mono font-medium text-stone-800 dark:text-stone-200 block">
              {currentModelIntelligence.formattedContext}
            </span>
          </div>

          {/* Rate Card */}
          <div className="space-y-0.5">
            <span className="text-stone-400 block font-medium">Token Pricing</span>
            <span className="font-mono font-semibold text-emerald-600 dark:text-emerald-400 block">
              ${currentModelIntelligence.promptPrice.toFixed(2)} / $
              {currentModelIntelligence.completionPrice.toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

interface ModelOptionRowProps {
  item: ModelIntelligence;
  isSelected: boolean;
  onSelect: () => void;
}

function ModelOptionRow({ item, isSelected, onSelect }: ModelOptionRowProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left p-2.5 flex items-center justify-between gap-3 transition-colors cursor-pointer ${
        isSelected
          ? "bg-stone-100 dark:bg-stone-800/80"
          : "hover:bg-stone-50 dark:hover:bg-stone-800/40"
      }`}
    >
      <div className="flex items-center gap-2.5 min-w-0 flex-1">
        {/* Checkmark or Radio circle */}
        <div
          className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 border transition-colors ${
            isSelected
              ? "bg-stone-900 border-stone-900 dark:bg-stone-100 dark:border-stone-100 text-white dark:text-stone-900"
              : "border-stone-300 dark:border-stone-700"
          }`}
        >
          {isSelected && <Check className="w-2.5 h-2.5 stroke-[3]" />}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-semibold text-stone-900 dark:text-stone-100">
              {item.name}
            </span>

            {/* Direct Google vs OpenRouter Route Pill */}
            {item.integrationRoute === "google_direct" ? (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded text-[10px] font-semibold bg-sky-50 text-sky-700 border border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800/50">
                <Radio className="w-2 h-2 text-sky-600" />
                Google (Direct)
              </span>
            ) : (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/50">
                <Share2 className="w-2 h-2 text-emerald-600" />
                OpenRouter
                <span className="font-normal opacity-75">· {item.underlyingProvider}</span>
              </span>
            )}

            {/* Reasoning Badge */}
            {item.isReasoning ? (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded text-[10px] font-semibold bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800/50">
                <Brain className="w-2.5 h-2.5" />
                {item.reasoningLabel}
              </span>
            ) : (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded text-[10px] font-medium bg-stone-100 text-stone-500 border border-stone-200 dark:bg-stone-800 dark:text-stone-400 dark:border-stone-700">
                <Zap className="w-2.5 h-2.5 text-stone-400" />
                Standard
              </span>
            )}

            {/* Structured Output Pill */}
            {item.supportsStructuredOutputs ? (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded text-[10px] font-semibold bg-teal-50 text-teal-700 border border-teal-200 dark:bg-teal-950/40 dark:text-teal-300 dark:border-teal-800/50" title="Supports native structured outputs (JSON schema)">
                <Layers className="w-2.5 h-2.5" />
                Structured Output
              </span>
            ) : null}

            {/* Curated */}
            {item.isCurated && (
              <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 dark:text-amber-400 font-medium">
                <Sparkles className="w-2.5 h-2.5" />
                Curated
              </span>
            )}
          </div>

          <div className="text-[11px] font-mono text-stone-400 dark:text-stone-500 truncate mt-0.5">
            {item.id}
          </div>
        </div>
      </div>

      {/* Right meta */}
      <div className="text-right shrink-0">
        <div className="text-[11px] font-mono text-stone-700 dark:text-stone-300">
          {item.formattedContext}
        </div>
        <div className="text-[10px] font-mono text-emerald-600 dark:text-emerald-400 font-semibold">
          ${item.promptPrice.toFixed(2)} / ${item.completionPrice.toFixed(2)}
        </div>
      </div>
    </button>
  );
}
